import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["indeterminate"];
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useRef } from 'react';
import { StyledCheckbox } from './Checkbox.styles';

/**
 * A native input element with
 * [`type="checkbox"`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox).
 * In general, you will want to use this with the LabeledControl component.
 *
 * @since 1.9.0
 */

export var Checkbox = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var indeterminate = _ref.indeterminate,
    rest = _objectWithoutProperties(_ref, _excluded);
  var defaultInnerRef = useRef();
  var innerRef = forwardedRef || defaultInnerRef;
  useEffect(function () {
    if (innerRef.current) {
      innerRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, innerRef]);
  return /*#__PURE__*/React.createElement(StyledCheckbox, _extends({
    "aria-checked": indeterminate ? 'mixed' : undefined,
    ref: innerRef
  }, rest));
});
Checkbox.displayName = 'Checkbox';
Checkbox.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * @deprecated Use the native `id` attributed if you want to manually set the id. Otherwise,
   * let LabeledControl set this for you.
   */
  controlId: PropTypes.string,
  /**
   * Sets the checked state of the input and uses it as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  checked: PropTypes.bool,
  /**
   * Sets the checked state of the input and uses it as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultChecked: PropTypes.bool,
  /**
   * Displays the input in a disabled state. Can also be inherited from a FieldSet parent.
   */
  disabled: PropTypes.bool,
  /**
   * When true, the checkbox is shown in the indeterminate state (also known as a
   * "partial check"). It's most commonly used above a list of related checkboxes
   * to indicate that some — but not all — of them are checked.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#Indeterminate
   *
   * It's recommended that you only use the indeterminate state with controlled checkboxes.
   *
   * @since 8.17.0
   */
  indeterminate: PropTypes.bool,
  /**
   * Used for hiding the checkbox while maintaining accessibility
   * (primarily used by the Switch component which overrides styles):
   * https://accessibilitytips.com/2008/03/04/positioning-content-offscreen/
   *
   * @ignore
   */
  offscreen: PropTypes.bool,
  /**
   * Indicates the input is required. Can also be inherited from a FieldSet parent.
   */
  required: PropTypes.bool,
  /**
   * The HTML input type.
   * @ignore
   */
  type: PropTypes.string
} : {};
Checkbox.defaultProps = {
  type: 'checkbox'
};